import { Controller } from "@hotwired/stimulus"

import { prefersReducedMotion } from "../../lib/prefers-reduced-motion"

/**
 * AI Thinking Orb — a particle orb that alternates between a rotating 3D
 * sphere and the Conek brand mark. Pure canvas 2D, no dependencies; Stimulus
 * port of the React `ThinkingOrb` reference (GA programme — companion polish).
 *
 * Usage:
 *   <canvas data-controller="ai--thinking-orb"
 *           data-ai--thinking-orb-size-value="22"
 *           aria-hidden="true"></canvas>
 *
 * Values:
 *   - size          canvas square size in px (default 120)
 *   - particleCount default scales with size (size × 4, min 80, max 320;
 *                   below 40px "compact": size × 2, min 36 — see
 *                   COMPACT_MAX_SIZE)
 *   - color         particle hex color (default Conek teal #3BBFC6)
 *   - speed         global speed multiplier (default 1)
 *   - mode          "auto" cycles sphere↔logo; "sphere"/"logo" pin one state
 *
 * Behaviour notes:
 *   - prefers-reduced-motion renders the STATIC logo once and never starts
 *     the rAF loop.
 *   - The loop self-suspends while the canvas isn't actually visible
 *     (companion closed → aside is visibility:hidden; thinking row →
 *     display:none), polling cheaply until it reappears. Without this the
 *     orb would burn a rAF per frame forever behind a closed sidebar.
 */

// The Conek mark, rescaled to a 30×30 viewBox (source:
// app/frontend/icons/conek-logo.svg, first path — the teal symbol).
const LOGO_PATH_D =
  "M5 11.9963C5 15.3645 7.0574 18.3002 10.1032 19.8421L6.68084 26H10.5833L13.2311 21.236V26H16.5802V21.0568L19.4149 26H23.3426L19.8304 19.8753C22.9126 18.3427 25 15.3888 25 11.9963C25 7.02776 20.5228 3 14.9999 3C9.47719 3 5 7.02776 5 11.9963ZM13.3447 14.01L11.6251 17.1039C9.69151 16.0677 8.39623 14.1681 8.39623 11.9963C8.39623 8.71515 11.3528 6.05532 14.9999 6.05532C18.6471 6.05532 21.6038 8.71515 21.6038 11.9963C21.6038 14.2064 20.2623 16.1346 18.272 17.1579L16.4566 13.9923C16.1554 13.467 15.5517 13.1384 14.8946 13.1421C14.2376 13.1459 13.6384 13.4812 13.3447 14.01Z"

// Sampled logo points are pure geometry — cache per particle count so N orbs
// on a page (thinking row + empty state) don't re-rasterise the path.
const logoPointsCache = new Map()

function hexToRgb(hex) {
  const h = hex.replace("#", "")
  const expanded =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h
  const n = parseInt(expanded, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

// Samples points inside the logo path (30×30 viewBox), normalised to [-1, 1].
// Returns [] when the environment can't rasterise (no Path2D / no 2D context
// — e.g. jsdom), in which case the orb stays a plain sphere.
function sampleLogoPoints(count) {
  const cached = logoPointsCache.get(count)
  if (cached) return cached
  if (typeof Path2D === "undefined") return []

  const off = document.createElement("canvas")
  off.width = off.height = 30
  const ctx = off.getContext("2d")
  if (!ctx || typeof ctx.isPointInPath !== "function") return []

  const path = new Path2D(LOGO_PATH_D)
  const points = []
  const step = 0.42
  for (let y = 2.5; y <= 27; y += step) {
    for (let x = 4; x <= 26; x += step) {
      if (ctx.isPointInPath(path, x, y, "evenodd")) points.push({ x, y })
    }
  }
  // Fisher–Yates so the slice below is a spatially even selection.
  for (let i = points.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0
    ;[points[i], points[j]] = [points[j], points[i]]
  }
  const chosen = points
    .slice(0, count)
    .map((p) => ({ x: (p.x - 15) / 13.5, y: (p.y - 14.5) / 13.5 }))

  logoPointsCache.set(count, chosen)
  return chosen
}

// Fibonacci sphere: `count` evenly distributed 3D points.
function spherePoints(count) {
  const points = []
  const golden = Math.PI * (3 - Math.sqrt(5))
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1)
    const y = 1 - 2 * t
    const r = Math.sqrt(Math.max(0, 1 - y * y))
    const a = golden * i
    points.push({ x: Math.cos(a) * r, y, z: Math.sin(a) * r })
  }
  return points
}

const easeInOut = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

// Cycle phase durations, seconds.
const PHASES = Object.freeze([
  { name: "sphere", dur: 3.2 },
  { name: "toLogo", dur: 1.4 },
  { name: "logo", dur: 3.0 },
  { name: "toSphere", dur: 1.4 },
])
const CYCLE_TOTAL = PHASES.reduce((sum, p) => sum + p.dur, 0)

// How often to poll for visibility while suspended (ms).
const HIDDEN_POLL_MS = 300

// Below this size (px) the orb renders COMPACT: at 22px the full particle
// cloud (80+ dots of ~1px, plus the wobble and the halo) fills the mark's
// counter and reads as a blob. Compact uses fewer, finer dots and, as the
// logo forms, cross-fades them into the crisp filled mark — so a navbar- or
// thinking-row-sized orb still ends on a legible Conek symbol.
const COMPACT_MAX_SIZE = 40
const COMPACT_MIN_PARTICLES = 36
const DEFAULT_COLOR = "#3BBFC6"

export default class extends Controller {
  static values = {
    size: { type: Number, default: 120 },
    particleCount: { type: Number, default: 0 },
    color: { type: String, default: DEFAULT_COLOR },
    speed: { type: Number, default: 1 },
    mode: { type: String, default: "auto" },
  }

  connect() {
    const canvas = this.element
    if (!(canvas instanceof HTMLCanvasElement)) {
      console.warn("[ai--thinking-orb] must be attached to a <canvas>")
      return
    }
    this.ctx = canvas.getContext("2d")
    if (!this.ctx) return

    const size = this.sizeValue
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = size * dpr
    canvas.height = size * dpr
    canvas.style.width = `${size}px`
    canvas.style.height = `${size}px`
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.dpr = dpr

    this.compact = size < COMPACT_MAX_SIZE
    this.count =
      this.particleCountValue > 0
        ? this.particleCountValue
        : this.compact
          ? Math.max(COMPACT_MIN_PARTICLES, Math.round(size * 2))
          : Math.min(320, Math.max(80, Math.round(size * 4)))
    this.logoPath =
      this.compact && typeof Path2D !== "undefined"
        ? new Path2D(LOGO_PATH_D)
        : null
    this.rgb = hexToRgb(this.colorValue)
    this.logo = sampleLogoPoints(this.count)
    this.sphere = spherePoints(this.count)
    this.twinkle = Array.from(
      { length: this.count },
      () => Math.random() * Math.PI * 2,
    )
    // Desync multiple orbs so they don't pulse in lockstep.
    this.t0 = performance.now() - Math.random() * CYCLE_TOTAL * 1000
    this.rafId = null
    this.pollId = null

    if (prefersReducedMotion()) {
      // Static brand mark, no animation loop at all.
      this.draw(performance.now(), 1)
      return
    }
    this.frame = this.frame.bind(this)
    this.rafId = requestAnimationFrame(this.frame)
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.pollId) clearTimeout(this.pollId)
    this.rafId = null
    this.pollId = null
  }

  // visibility:hidden ancestors (closed sidebar) don't null offsetParent, so
  // prefer checkVisibility() where available; offsetParent covers
  // display:none (the thinking row) everywhere else.
  #isVisible() {
    const el = this.element
    if (typeof el.checkVisibility === "function") {
      return el.checkVisibility({
        visibilityProperty: true,
        opacityProperty: true,
      })
    }
    return el.offsetParent !== null
  }

  frame(now) {
    if (!this.#isVisible()) {
      // Suspend: no drawing, no rAF — just a cheap poll until re-shown.
      this.rafId = null
      this.pollId = setTimeout(() => {
        this.pollId = null
        this.rafId = requestAnimationFrame(this.frame)
      }, HIDDEN_POLL_MS)
      return
    }
    this.draw(now, null)
    this.rafId = requestAnimationFrame(this.frame)
  }

  // Renders one frame. `forcedMix` pins the sphere↔logo blend (used for the
  // reduced-motion static render); null derives it from the cycle clock.
  draw(now, forcedMix) {
    const size = this.sizeValue
    const ctx = this.ctx
    const sec = ((now - this.t0) / 1000) * this.speedValue
    const rot = sec * 0.7

    let mix
    if (forcedMix != null) mix = forcedMix
    else if (this.modeValue === "sphere") mix = 0
    else if (this.modeValue === "logo" || this.logo.length === 0) {
      mix = this.logo.length === 0 ? 0 : 1
    } else {
      const phase = this.#phaseAt(sec)
      if (phase.name === "sphere") mix = 0
      else if (phase.name === "logo") mix = 1
      else if (phase.name === "toLogo") mix = easeInOut(phase.t)
      else mix = 1 - easeInOut(phase.t)
    }

    const cx = size / 2
    const cy = size / 2
    const radius = size * 0.36
    const dotBase = this.compact ? 0.75 : Math.max(0.9, size / 110)
    // Compact: the dots give way to the filled mark as the logo forms.
    // Fully formed by mix 0.8, so the dots are gone before the mark settles
    // instead of haloing its edges.
    const markAlpha = this.logoPath
      ? Math.min(1, Math.max(0, (mix - 0.4) / 0.4))
      : 0
    const { r, g, b } = this.rgb

    ctx.clearRect(0, 0, size, size)

    for (let i = 0; i < this.count; i++) {
      const s = this.sphere[i]
      const sx3 = s.x * Math.cos(rot) - s.z * Math.sin(rot)
      const sz3 = s.x * Math.sin(rot) + s.z * Math.cos(rot)
      const depth = (sz3 + 1) / 2
      const sx = cx + sx3 * radius
      const sy = cy + s.y * radius

      const l = this.logo[i] ?? { x: 0, y: 0 }
      const wobble = this.compact
        ? 0
        : mix * Math.sin(sec * 2 + this.twinkle[i]) * 0.35
      const lx = cx + l.x * radius * 1.18 + wobble
      const ly = cy + l.y * radius * 1.18 + wobble * 0.6

      const x = sx + (lx - sx) * mix
      const y = sy + (ly - sy) * mix

      // Compact: a higher floor, or the back of the sphere vanishes at 20px.
      const alphaSphere = this.compact
        ? 0.55 + depth * 0.45
        : 0.25 + depth * 0.75
      const alphaLogo = 0.65 + 0.35 * Math.sin(sec * 3 + this.twinkle[i])
      const alpha =
        (alphaSphere + (alphaLogo - alphaSphere) * mix) * (1 - markAlpha)
      const dotSize = dotBase * (0.7 + 0.5 * (mix > 0.5 ? alphaLogo : depth))

      ctx.beginPath()
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha.toFixed(3)})`
      ctx.arc(x, y, dotSize, 0, Math.PI * 2)
      ctx.fill()
    }

    if (markAlpha > 0) {
      // The mark, in the same 30×30 space the logo points were sampled from.
      const k = (radius * 1.18) / 13.5
      ctx.save()
      // Snapped to the device pixel: a sub-pixel origin (~0.6px at 22px)
      // antialiased every edge of the mark into a blur.
      const snap = (v) => Math.round(v * this.dpr) / this.dpr
      ctx.translate(snap(cx - 15 * k), snap(cy - 14.5 * k))
      ctx.scale(k, k)
      ctx.fillStyle = `rgba(${r},${g},${b},${markAlpha.toFixed(3)})`
      ctx.fill(this.logoPath, "evenodd")
      ctx.restore()
    }

    // Soft halo while the brand mark is formed. Not when compact: at icon
    // size it only muddies the edges.
    if (mix > 0.5 && !this.compact) {
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.5)
      gradient.addColorStop(
        0,
        `rgba(${r},${g},${b},${((mix - 0.5) * 0.16).toFixed(3)})`,
      )
      gradient.addColorStop(1, "rgba(0,0,0,0)")
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, size, size)
    }
  }

  #phaseAt(sec) {
    let t = ((sec % CYCLE_TOTAL) + CYCLE_TOTAL) % CYCLE_TOTAL
    for (const phase of PHASES) {
      if (t < phase.dur) return { name: phase.name, t: t / phase.dur }
      t -= phase.dur
    }
    return { name: "sphere", t: 0 }
  }
}
